import React from 'react';

import { List, Tag } from 'antd';

import styled from 'styled-components';

import { showDateNoTime } from '@libs/NwMoment';

import { useUserPermission } from '@contexts/UserContext';

import NwDivider from '@components/Gui/NwDivider';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const StyledListItem = styled(List.Item)`
    .ant-list-item-meta {
        align-items: flex-start;
        .ant-list-item-meta-avatar {
            padding-right: 0.25rem 2rem 0 0;
            min-width: 10rem;
            .agency-type {
                text-align: center;
                text-transform: uppercase;
                font-size: ${(props) => props.theme.FontSizeSm};
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;

                .agency-type-line {
                    margin-bottom: 0.25rem;

                    .ant-tag {
                        font-size: ${(props) => props.theme.FontSizeXSm};
                        padding: 0 0.25rem;
                        line-height: 1.5;
                        margin-right: 0;
                    }
                }
            }
        }
        .ant-list-item-meta-content {
            .ant-list-item-meta-title {
                .agency-title-container {
                    text-transform: capitalize;
                    cursor: pointer;

                    &.agency-dropped {
                        ${(props) => props.theme.RedLineThrough(1)};
                    }
                }
            }
            .ant-list-item-meta-description {
                color: ${(props) => props.theme.TextColor};
                font-size: ${(props) => props.theme.FontSizeSm};
                .agency-description-container {
                    font-size: ${(props) => props.theme.FontSizeSm};
                    padding-right: 2rem;

                    .agency-booker {
                        margin-top: 0.25rem;
                        font-weight: bold;
                    }
                    .agency-dates,
                    .agency-contract {
                        text-transform: uppercase;
                        color: ${(props) => props.theme.TextColorMuted};
                    }
                    .agency-notes {
                        margin-bottom: 0.25rem;
                    }
                    .agency-commission {
                        margin-top: 0.25rem;
                        font-weight: bold;
                    }
                }
            }
        }
    }
`;

const ModelAgenciesListItem = ({ handleCommissions, handleEdit, handleRemove, modelAgency, onNameClick }) => {
    const accessAccounting = useUserPermission('AccessAccounting');
    const isPlacement = modelAgency.TypeName?.toLowerCase() === 'placement';
    const hasPercentage = modelAgency.Percentage && modelAgency.Percentage > 0;

    const ModelAgencyIcon = ({ type }) => {
        switch (type.toLowerCase()) {
            case 'mother':
                return <NwIcon size="xl" icon={byPrefixAndName.fasl['medal']} />;
            case 'placement':
                return <NwIcon size="xl" icon={byPrefixAndName.fasl['handshake']} />;
            default:
                return <NwIcon size="xl" icon={byPrefixAndName.fasl['globe']} />;
        }
    };

    const ModelAgencyAvatar = () => (
        <div className="agency-type" style={{ opacity: modelAgency.Dropped ? '.4' : '1' }}>
            {modelAgency.Dropped && (
                <span className="agency-type-line">
                    <Tag color={`${StyleVariables.DangerColor}`}>DROPPED</Tag>
                </span>
            )}
            <span className="agency-type-line">{modelAgency.TypeName.toUpperCase()}</span>
            <span className="agency-type-line">
                <ModelAgencyIcon type={modelAgency.TypeName} />
            </span>
            {modelAgency.Label && (
                <span className="agency-type-line">
                    <Tag color={`#${modelAgency.Label.Color}`}>{modelAgency.Label.Name}</Tag>
                </span>
            )}
        </div>
    );

    const drowDownMenuActions = [
        {
            name: '',
            key: 'edit',
            pinned: true,
            icon: byPrefixAndName.fasl['pen'],
            action: () => handleEdit(modelAgency),
        },
        {
            name: 'delete',
            type: 'delete',
            key: 'delete',
            icon: byPrefixAndName.fasl['trash-alt'],
            confirmationPlacement: 'left',
            action: () => handleRemove(modelAgency),
        },
    ];

    if (handleCommissions && (hasPercentage || isPlacement)) {
        const actionName = isPlacement ? 'show commissions due' : 'show moth. ag. comm.';
        drowDownMenuActions.push({
            name: actionName,
            key: 'commissions',
            icon: byPrefixAndName.fasl['percent'],
            action: () => handleCommissions(modelAgency),
        });
    }

    const ModelAgencyDescription = () => {
        return (
            <div className={`agency-description-container${modelAgency.Dropped ? ' agency-dropped' : ''}`}>
                {modelAgency.Notes && (
                    <>
                        <NwDivider lessMargin>Booking Notes</NwDivider>
                        <div className="agency-notes">{modelAgency.Notes}</div>
                    </>
                )}
                {accessAccounting && modelAgency.AccountingNotes && (
                    <>
                        <NwDivider lessMargin>Accounting Notes</NwDivider>
                        <div className="agency-notes-accounting-notes">{modelAgency.AccountingNotes}</div>
                    </>
                )}
                {modelAgency.Booker && <div className="agency-booker">Booker: {modelAgency.Booker}</div>}
                {modelAgency.ContractDate && (
                    <div className="agency-contract">Contract signed on {showDateNoTime(modelAgency.ContractDate)}</div>
                )}
                {!modelAgency.DateIn && modelAgency.DateOut && (
                    <div className="agency-dates">left on {showDateNoTime(modelAgency.DateOut)}</div>
                )}
                {modelAgency.DateIn && !modelAgency.DateOut && (
                    <div className="agency-dates">since {showDateNoTime(modelAgency.DateIn)}</div>
                )}
                {modelAgency.DateIn && modelAgency.DateOut && (
                    <div className="agency-dates">{`${showDateNoTime(modelAgency.DateIn)} > ${showDateNoTime(modelAgency.DateOut)}`}</div>
                )}
                {modelAgency.Percentage > 0 && (
                    <div className="agency-commission">{`${modelAgency.Percentage}% comm. ${modelAgency.OnGross ? ' on model gross' : ' on model net'}`}</div>
                )}
            </div>
        );
    };

    const ModelAgencyTitle = () => (
        <span className={`agency-title-container${modelAgency.Dropped ? ' agency-dropped' : ''}`} onClick={onNameClick}>
            <strong>{modelAgency.AgencyName.toLowerCase()}</strong>
            {modelAgency.Towns
                ? ` - ${modelAgency.Towns.split(',')
                      .map((t) => t.toLowerCase())
                      .join(', ')}`
                : ''}
        </span>
    );

    return (
        <StyledListItem actions={[<NwDropDownMenu actions={drowDownMenuActions} />]}>
            <List.Item.Meta
                avatar={<ModelAgencyAvatar />}
                title={<ModelAgencyTitle />}
                description={<ModelAgencyDescription />}
            />
        </StyledListItem>
    );
};

export default ModelAgenciesListItem;
